import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw, Loader2, Plus, Trash2, ImageIcon, Inbox } from "lucide-react";
import { adminLogin, fetchOrders, fetchGallery, createGalleryItem, deleteGalleryItem } from "@/lib/api";

const TOKEN_KEY = "sweetrose_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("orders");

  const [orders, setOrders] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async (t) => {
    setLoading(true);
    try {
      const [o, g] = await Promise.all([fetchOrders(t), fetchGallery()]);
      setOrders(o);
      setGallery(g);
      setAuthed(true);
    } catch (err) {
      toast.error("Session expired. Please sign in again.");
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      await adminLogin(input.trim());
      localStorage.setItem(TOKEN_KEY, input.trim());
      setToken(input.trim());
      await loadAll(input.trim());
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid token.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setOrders([]);
    setGallery([]);
    setInput("");
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#F5DCD5] flex items-center justify-center px-6"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={handleLogin}
          className="bg-white border border-[#EFE8E8] rounded-lg p-10 w-full max-w-md"
          data-testid="admin-login-form"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C] mb-4">
            Sweet Rose Admin
          </div>
          <h1 className="font-serif-display text-3xl text-[#362C2C] mb-2">Welcome back.</h1>
          <p className="text-sm text-[#5C5252] mb-8">
            Enter your admin token to manage inquiries and the gallery.
          </p>
          <label className="block text-sm text-[#5C5252] mb-2">Admin token</label>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="••••••••••"
            className="w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-2 text-[#362C2C] transition-colors"
            data-testid="admin-token-input"
          />
          <button
            type="submit"
            className="mt-8 w-full bg-[#B37C7C] text-white py-3 rounded-md hover:bg-[#9A6565] transition-all tracking-wide text-sm"
            data-testid="admin-login-submit"
          >
            Sign in
          </button>
          <a
            href="/"
            className="mt-6 block text-center text-xs text-[#5C5252] hover:text-[#B37C7C]"
            data-testid="admin-back-home"
          >
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5DCD5]" data-testid="admin-dashboard">
      <header className="border-b border-[#EFE8E8] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C]">
              Sweet Rose · Admin
            </div>
            <div className="font-serif-display text-2xl text-[#362C2C]">Studio</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadAll(token)}
              className="inline-flex items-center gap-2 text-sm text-[#5C5252] hover:text-[#B37C7C] px-3 py-2 rounded-md"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm border border-[#EFE8E8] text-[#5C5252] hover:text-[#B37C7C] hover:border-[#B37C7C] px-3 py-2 rounded-md transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex gap-2 pb-3 -mt-1">
          <TabBtn
            active={tab === "orders"}
            onClick={() => setTab("orders")}
            icon={<Inbox size={14} />}
            label={`Inquiries (${orders.length})`}
            testid="admin-tab-orders"
          />
          <TabBtn
            active={tab === "gallery"}
            onClick={() => setTab("gallery")}
            icon={<ImageIcon size={14} />}
            label={`Gallery (${gallery.length})`}
            testid="admin-tab-gallery"
          />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#5C5252]">
            <Loader2 className="animate-spin mr-2" size={18} />
            Loading…
          </div>
        ) : tab === "orders" ? (
          <OrdersTable orders={orders} />
        ) : (
          <GalleryManager
            items={gallery}
            token={token}
            onChange={() => loadAll(token)}
          />
        )}
      </main>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all ${
        active
          ? "bg-[#F7EBEB] text-[#B37C7C] font-medium"
          : "text-[#5C5252] hover:text-[#B37C7C]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function OrdersTable({ orders }) {
  if (orders.length === 0) {
    return (
      <div className="bg-white border border-[#EFE8E8] rounded-lg p-16 text-center">
        <div className="font-serif-display text-2xl text-[#362C2C] mb-2">
          No inquiries yet.
        </div>
        <p className="text-sm text-[#5C5252]">
          New order requests will appear here as they're submitted.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EFE8E8] rounded-lg overflow-hidden">
      <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F7EBEB]/60 text-[10px] uppercase tracking-[0.22em] text-[#5C5252] font-medium">
        <div className="col-span-3">Client</div>
        <div className="col-span-2">Occasion</div>
        <div className="col-span-2">Event date</div>
        <div className="col-span-2">Quantity</div>
        <div className="col-span-2">Submitted</div>
        <div className="col-span-1 text-right">Status</div>
      </div>
      <div className="divide-y divide-[#EFE8E8]">
        {orders.map((o) => (
          <div
            key={o.id}
            className="px-6 py-5 hover:bg-[#F5DCD5] transition-colors"
            data-testid={`admin-order-row-${o.id}`}
          >
            <div className="grid grid-cols-12 gap-4 items-start">
              <div className="col-span-3">
                <div className="font-medium text-[#362C2C]">{o.name}</div>
                <div className="text-xs text-[#5C5252] mt-0.5">{o.email}</div>
                {o.phone && <div className="text-xs text-[#5C5252]">{o.phone}</div>}
              </div>
              <div className="col-span-2 text-sm text-[#362C2C]">{o.occasion}</div>
              <div className="col-span-2 text-sm text-[#5C5252]">{o.event_date || "—"}</div>
              <div className="col-span-2 text-sm text-[#5C5252]">{o.quantity || "—"}</div>
              <div className="col-span-2 text-xs text-[#5C5252]">
                {new Date(o.created_at).toLocaleString()}
              </div>
              <div className="col-span-1 text-right">
                <span className="inline-block text-[10px] uppercase tracking-[0.18em] bg-[#F7EBEB] text-[#B37C7C] px-2 py-1 rounded">
                  {o.status}
                </span>
              </div>
            </div>
            {(o.theme || o.details) && (
              <div className="grid grid-cols-12 gap-4 mt-4 pt-4 border-t border-[#EFE8E8]/60">
                {o.theme && (
                  <div className="col-span-4 text-xs">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5252] mb-1">
                      Theme
                    </div>
                    <div className="text-[#362C2C]">{o.theme}</div>
                  </div>
                )}
                {o.details && (
                  <div className="col-span-8 text-xs">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5252] mb-1">
                      Details
                    </div>
                    <div className="text-[#362C2C] leading-relaxed">{o.details}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryManager({ items, token, onChange }) {
  const [form, setForm] = useState({ title: "", image: "", blurb: "", category: "" });
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.image.trim()) {
      toast.error("Title and image URL are required.");
      return;
    }
    setSubmitting(true);
    try {
      await createGalleryItem(
        {
          title: form.title.trim(),
          image: form.image.trim(),
          blurb: form.blurb.trim(),
          category: form.category.trim() || "General",
        },
        token
      );
      setForm({ title: "", image: "", blurb: "", category: "" });
      toast.success("Photo added.");
      onChange();
    } catch {
      toast.error("Could not add photo.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    setDeletingId(id);
    try {
      await deleteGalleryItem(id, token);
      toast.success("Removed.");
      onChange();
    } catch {
      toast.error("Could not remove.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4">
        <form
          onSubmit={submit}
          className="bg-white border border-[#EFE8E8] rounded-lg p-6 space-y-5 sticky top-36"
          data-testid="admin-gallery-form"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C] mb-2">
              Add Photo
            </div>
            <h2 className="font-serif-display text-2xl text-[#362C2C] mb-1">
              New gallery item
            </h2>
            <p className="text-xs text-[#5C5252]">
              Paste an image URL (e.g. from your cloud drive, Imgur, or Instagram).
            </p>
          </div>

          <GField label="Title *">
            <input
              value={form.title}
              onChange={update("title")}
              placeholder="e.g. Emily's bridal shower"
              className={inputCls}
              data-testid="admin-gallery-title"
              required
            />
          </GField>
          <GField label="Image URL *">
            <input
              value={form.image}
              onChange={update("image")}
              placeholder="https://…"
              className={inputCls}
              data-testid="admin-gallery-image"
              required
            />
          </GField>
          <GField label="Category">
            <input
              value={form.category}
              onChange={update("category")}
              placeholder="Weddings, Birthdays, Seasonal…"
              className={inputCls}
              data-testid="admin-gallery-category"
            />
          </GField>
          <GField label="Short description">
            <textarea
              value={form.blurb}
              onChange={update("blurb")}
              rows={3}
              placeholder="A one-line story for this set."
              className={`${inputCls} resize-none`}
              data-testid="admin-gallery-blurb"
            />
          </GField>

          {form.image && (
            <div className="border border-[#EFE8E8] rounded-md overflow-hidden aspect-[4/5] bg-[#F7EBEB]">
              <img
                src={form.image}
                alt="preview"
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.style.opacity = "0.2")}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#B37C7C] text-white py-3 rounded-md hover:bg-[#9A6565] transition-all tracking-wide text-sm disabled:opacity-60"
            data-testid="admin-gallery-submit"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            Add to gallery
          </button>
        </form>
      </div>

      <div className="lg:col-span-8">
        {items.length === 0 ? (
          <div className="bg-white border border-[#EFE8E8] rounded-lg p-16 text-center">
            <div className="font-serif-display text-2xl text-[#362C2C] mb-2">
              Gallery is empty.
            </div>
            <p className="text-sm text-[#5C5252]">
              Add your first photo using the form.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="admin-gallery-list">
            {items.map((item) => (
              <div
                key={item.id}
                className="group bg-white border border-[#EFE8E8] rounded-lg overflow-hidden"
                data-testid={`admin-gallery-item-${item.id}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#F7EBEB] relative">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => remove(item.id)}
                    disabled={deletingId === item.id}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm border border-[#EFE8E8] text-[#B35B5B] p-2 rounded-md opacity-0 group-hover:opacity-100 hover:bg-[#B35B5B] hover:text-white transition-all"
                    data-testid={`admin-gallery-delete-${item.id}`}
                    aria-label="Delete"
                  >
                    {deletingId === item.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
                <div className="p-4">
                  {item.category && (
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#B37C7C] mb-1">
                      {item.category}
                    </div>
                  )}
                  <div className="font-serif-display text-lg text-[#362C2C]">
                    {item.title}
                  </div>
                  {item.blurb && (
                    <div className="text-xs text-[#5C5252] mt-1 line-clamp-2">
                      {item.blurb}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[#EFE8E8] focus:border-[#B37C7C] outline-none px-0 py-2 text-[#362C2C] placeholder:text-[#5C5252]/50 text-sm transition-colors";

function GField({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-[#5C5252] mb-1">{label}</label>
      {children}
    </div>
  );
}
